import { Component, input } from '@angular/core';
import { NUMERIC_AGGREGATION, type BoardMetricDisplayChip } from '../../models/board-stage-metric.model';

@Component({
  selector: 'app-board-stage-metrics',
  standalone: true,
  template: `
    <div class="stage-metrics-chips" [class.stage-metrics-chips--tinted]="onTintedHeader()">
      @for (chip of chips(); track $index) {
        @if (numericChipText(chip); as numText) {
          <span
            class="metric-chip metric-chip--sum"
            [attr.title]="numericChipTitle(chip, numText) || null"
          >
            @if (numericChipFieldLabel(chip); as fieldLabel) {
              <span class="metric-chip-sum-field">{{ fieldLabel }}:</span>
            }
            <span class="metric-chip-sum-value">{{ aggregationGlyph(chip.aggregation) }} {{ numText }}</span>
          </span>
        }
      }
    </div>
  `,
  styles: [
    `
      .stage-metrics-chips {
        display: flex;
        flex-wrap: wrap;
        align-items: center;
        gap: 6px;
        min-width: 0;
      }

      .metric-chip {
        display: inline-flex;
        align-items: center;
        max-width: 100%;
        padding: 3px 8px;
        border-radius: 999px;
        font-size: 11px;
        font-weight: 600;
        line-height: 1.25;
        letter-spacing: 0.01em;
      }

      .stage-metrics-chips:not(.stage-metrics-chips--tinted) .metric-chip {
        background: rgba(255, 255, 255, 0.96);
        border: 1px solid rgba(15, 23, 42, 0.12);
        color: #111827;
      }

      .stage-metrics-chips--tinted .metric-chip {
        background: rgba(15, 23, 42, 0.28);
        border: 1px solid rgba(255, 255, 255, 0.38);
        color: #ffffff;
      }

      .metric-chip--sum {
        font-variant-numeric: tabular-nums;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
        gap: 4px;
      }

      .metric-chip-sum-field {
        flex-shrink: 1;
        min-width: 0;
        overflow: hidden;
        text-overflow: ellipsis;
        font-weight: 600;
        opacity: 0.92;
      }

      .metric-chip-sum-value {
        flex-shrink: 0;
        font-variant-numeric: tabular-nums;
      }
    `,
  ],
})
export class BoardStageMetricsComponent {
  /** True quando o header da coluna usa texto claro sobre cor saturada. */
  readonly onTintedHeader = input(false);
  readonly chips = input<BoardMetricDisplayChip[]>([]);

  aggregationGlyph(aggregation: number): string {
    switch (aggregation) {
      case NUMERIC_AGGREGATION.Sum:
        return 'Σ';
      case NUMERIC_AGGREGATION.Average:
        return 'média';
      case NUMERIC_AGGREGATION.Min:
        return 'min';
      case NUMERIC_AGGREGATION.Max:
        return 'max';
      case NUMERIC_AGGREGATION.Product:
        return 'Π';
      case NUMERIC_AGGREGATION.SubtractOldestMinusRest:
        return '−';
      case NUMERIC_AGGREGATION.DivideOldestByProductRest:
        return '÷';
      default:
        return '';
    }
  }

  numericChipText(chip: BoardMetricDisplayChip): string | null {
    const v = chip.value;
    if (v == null || (typeof v === 'number' && !Number.isFinite(v))) {
      return '—';
    }
    if (chip.formatCurrency) {
      return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(v);
    }
    // Respeita as casas decimais do campo de origem. Fallback (sem schema definido): até 4 casas.
    const dp = chip.decimalPlaces;
    if (dp == null) {
      return new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 4 }).format(v);
    }
    return new Intl.NumberFormat('pt-BR', {
      minimumFractionDigits: dp,
      maximumFractionDigits: dp,
    }).format(v);
  }

  numericChipTitle(chip: BoardMetricDisplayChip, formatted: string): string {
    const label = chip.label.trim();
    const g = this.aggregationGlyph(chip.aggregation);
    if (!label) return `${g} ${formatted}`.trim();
    return `${label}: ${g} ${formatted}`;
  }

  numericChipFieldLabel(chip: BoardMetricDisplayChip): string | null {
    const t = chip.label.trim();
    return t.length ? t : null;
  }
}
